import React, { useMemo, useState } from 'react';
import { Form, Button, Input, Card, Result, Divider, Checkbox, Select, App } from "antd";
import { apis, hooks } from "@vote4dance/judging-sdk";
import { Localization, _T } from "../src/Localization";
var useSession = hooks.sessions.useSession;
var Session = apis.sessions.Session;
import { Link, useLocation } from 'react-router-dom';
import { GoogleReCaptcha, GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { getSafeRedirectUrl } from "../src/safeRedirect";
import { Loading } from "./Loading";
import { getBackendResponse, isCaptchaFailure, isRegisterUserError } from "../src/errorResponse";
import { useCaptchaKey } from '../hooks/useCaptchaKey';
const NAME_PATTERN = /^[\p{L}\p{M}\s'-]+$/u;
export const RegisterForm = ({ reload = true, compact = false }) => {
    var _a;
    const [loading, setLoading] = useState(false);
    const sessionState = useSession();
    const location = useLocation();
    const [form] = Form.useForm();
    const captchaKey = useCaptchaKey();
    const captchaEnabled = captchaKey !== null;
    const { notification: notificationApi } = App.useApp();
    const countryOptions = useMemo(() => {
        const names = Localization.getCountryNames();
        return Object.entries(names).map(([code, label]) => ({ value: code, label }));
    }, []);
    const defaultCountry = useMemo(() => Localization.guessUserCountry(), []);
    if (captchaKey === undefined) {
        return React.createElement(Loading, null);
    }
    const onFinish = (values) => {
        setLoading(true);
        const newUser = {
            user_email: values['user_email'],
            user_pass: values['user_pass'],
            user_firstname: values['user_firstname'],
            user_lastname: values['user_lastname'],
            user_country: values['user_country'],
        };
        const captchaToken = form.getFieldValue('captcha');
        if (captchaToken) {
            newUser['recaptcha_token'] = captchaToken;
        }
        Session.get()
            .register(newUser)
            .then(() => {
            var _a;
            const redirectTarget = getSafeRedirectUrl((_a = location.state) === null || _a === void 0 ? void 0 : _a.from);
            if (redirectTarget) {
                window.location.assign(redirectTarget);
            }
            else if (reload) {
                window.location.reload();
            }
        })
            .catch((err) => {
            setLoading(false);
            console.error('Registration failed', err);
            const response = getBackendResponse(err);
            const message = isRegisterUserError(response)
                ? _T('register.errors.email-already-exists')
                : isCaptchaFailure(response)
                    ? _T('common.captcha')
                    : _T('register.errors.generic-error');
            notificationApi.error({ title: message });
        });
    };
    const setTokenFunc = (token) => form.setFieldValue('captcha', token);
    const tailFormItemLayout = {
        wrapperCol: { xs: { span: 24, offset: 0 }, sm: { span: 16, offset: 8 } },
    };
    if (sessionState === null || sessionState === void 0 ? void 0 : sessionState.isLoggedIn) {
        return React.createElement(Result, { status: "success", title: _T('login.success') });
    }
    return (React.createElement("div", { style: {
            display: 'flex',
            justifyContent: 'center',
            padding: compact ? '2vh 16px' : '6vh 16px',
        } },
        React.createElement(Card, { title: _T('register.title'), style: {
                width: '100%',
                maxWidth: 720,
                minWidth: 280,
            } },
            React.createElement(Form, { name: "RegisterForm", form: form, labelCol: { span: 8 }, wrapperCol: { span: 16 }, onFinish: onFinish, autoComplete: "off", initialValues: {
                    user_country: defaultCountry && ((_a = countryOptions.find(o => o.value.toUpperCase() === defaultCountry.toUpperCase())) === null || _a === void 0 ? void 0 : _a.value)
                } },
                React.createElement(Form.Item, { label: _T('profile.details.user_firstname'), name: "user_firstname", rules: [
                        { required: true, message: _T('common.required') },
                        { pattern: NAME_PATTERN, message: _T('register.errors.name-invalid') },
                    ] },
                    React.createElement(Input, { "data-testid": "user_firstname", autoFocus: true, autoComplete: 'given-name' })),
                React.createElement(Form.Item, { label: _T('profile.details.user_lastname'), name: "user_lastname", rules: [
                        { required: true, message: _T('common.required') },
                        { pattern: NAME_PATTERN, message: _T('register.errors.name-invalid') },
                    ] },
                    React.createElement(Input, { "data-testid": "user_lastname", autoComplete: 'family-name' })),
                React.createElement(Form.Item, { label: _T('login.email'), name: "user_email", rules: [{ required: true, message: _T('login.emailInputMessage') }] },
                    React.createElement(Input, { "data-testid": "email", type: "email", autoComplete: 'email' })),
                React.createElement(Form.Item, { name: "user_country", label: _T('profile.details.user_country'), rules: [{ required: true, message: _T('common.required') }] },
                    React.createElement(Select, { allowClear: true, showSearch: true, optionFilterProp: "label", options: countryOptions })),
                React.createElement(Form.Item, { label: _T('login.password'), name: "user_pass", rules: [{ required: true, message: _T('login.passwordInputMessage') }] },
                    React.createElement(Input.Password, { "data-testid": "password", type: "password", autoComplete: 'new-password' })),
                React.createElement(Form.Item, { name: "user_pass_confirm", label: _T('profile.password.confirm'), dependencies: ['user_pass'], hasFeedback: true, rules: [
                        { required: true, message: _T('common.required') },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('user_pass') === value)
                                    return Promise.resolve();
                                return Promise.reject(new Error(_T('register.errors.password-mismatch')));
                            },
                        }),
                    ] },
                    React.createElement(Input.Password, { "data-testid": "passwordConfirm", type: "password", autoComplete: 'new-password' })),
                React.createElement(Form.Item, { name: "agreement", valuePropName: "checked", rules: [
                        { validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error(_T('common.required'))) },
                    ], ...tailFormItemLayout },
                    React.createElement(Checkbox, null,
                        React.createElement("div", { style: { display: 'flex', flexWrap: 'wrap', gap: '0.25rem', justifyContent: 'center', alignItems: 'baseline' } },
                            React.createElement("span", null, _T('register.agree')),
                            React.createElement("div", { style: { display: 'flex', flexWrap: 'wrap', gap: '0.25rem', justifyContent: 'center' } },
                                React.createElement("a", { href: "../home/terms", target: "_blank" }, _T('register.terms')),
                                React.createElement("span", null, _T('common.and').toLowerCase()),
                                React.createElement("a", { href: "../home/privacy", target: "_blank" }, _T('register.privacy')))))),
                captchaEnabled &&
                    React.createElement(React.Fragment, null,
                        React.createElement(Form.Item, { label: "Captcha", name: "captcha", rules: [{ required: true, message: _T('common.required') }], hidden: true },
                            React.createElement(Input, null)),
                        React.createElement(GoogleReCaptchaProvider, { reCaptchaKey: captchaKey },
                            React.createElement(GoogleReCaptcha, { onVerify: token => setTokenFunc(token) }))),
                React.createElement(Form.Item, { wrapperCol: { span: 24 }, style: { textAlign: 'center' } },
                    React.createElement(Button, { type: "primary", htmlType: "submit", loading: loading, "data-testid": "submit" }, _T('register.title')))),
            React.createElement(Divider, null),
            React.createElement("span", null,
                _T('register.existing'),
                " "),
            React.createElement(Link, { to: '/login', state: location.state }, _T('login.title')))));
};
